import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from '@phosphor-icons/react';

import { CardSetupForm } from '@/components/notices/CardSetupForm';

// PublicCardSetup — the card-entry step of the member app's recurring-payment
// flow, rendered in the system browser.
//
// Why a web page rather than in-app card fields: the app has no Stripe SDK and
// already hands payment off to the browser (the home tab opens /pay/<token> the
// same way), so reusing that pattern keeps one Stripe.js card form in the codebase
// instead of a second native one, and adds no native dependency.
//
// Why no login here: the app is already authenticated and calls
// POST /me/subscriptions/{id}/card-setup itself, which enforces the caller's
// self/guardian scope AND records the recurring mandate. What it gets back is a
// SetupIntent client_secret — a credential Stripe intends to be handed to a
// browser, and which permits exactly one thing: attaching a card to that one
// SetupIntent. It reads no member data, so this page needs no session of its own.
//
// The secret arrives in the URL FRAGMENT, never the query string: fragments are
// not sent to the server, so it stays out of access logs, and out of the Referer
// header on any outbound navigation.
export default function PublicCardSetup() {
  const { t } = useTranslation('common');
  const [done, setDone] = useState(false);

  const params = useMemo(() => {
    // location.hash — e.g. "#cs=seti_..._secret_...&acct=acct_123"
    const raw = window.location.hash.replace(/^#/, '');
    const sp = new URLSearchParams(raw);
    return {
      clientSecret: sp.get('cs') ?? '',
      stripeAccountId: sp.get('acct') ?? '',
    };
  }, []);

  // Drop the secret from the address bar once read, so it isn't left sitting in
  // browser history or visible over someone's shoulder.
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  if (done) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 p-6 text-center">
        <CheckCircle size={48} className="text-primary" weight="fill" />
        <h1 className="text-2xl font-extrabold tracking-tight">
          {t('notices.card.saved.title')}
        </h1>
        <p className="text-sm text-foreground/70">{t('notices.card.saved.body')}</p>
      </main>
    );
  }

  if (!params.clientSecret) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-xl font-bold tracking-tight">{t('notices.card.linkInvalid.title')}</h1>
        <p className="text-sm text-foreground/70">{t('notices.card.linkInvalid.body')}</p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-5 p-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-extrabold tracking-tight">
          {t('notices.card.mandate.heading')}
        </h1>
        <p className="text-sm text-foreground/70">{t('notices.card.setupIntro')}</p>
      </div>
      <CardSetupForm
        clientSecret={params.clientSecret}
        stripeAccountId={params.stripeAccountId}
        onDone={() => setDone(true)}
        onCancel={() => window.close()}
      />
    </main>
  );
}
